import { type Page, toListQuery } from '@hello-aria/makalu-api-client'
import { Equal, QueryStringInput } from '@hello-aria/query-string'
import { isEmpty, isNil } from 'lodash'
import { Company } from '../models/companyModel'

import {
    CancelLoan,
    CreateLoanGateway,
    CreatePaymentRetry,
    CreatePaymentRetryGateway,
    DeleteLoan,
    Loan,
    LoanGateway,
    UpdateLoanGateway,
    CreateLoanPayload,
    UpdateLoanPayload,
    editableLoanStatuses
} from '../models/loanModels'
import { toPaymentDestination, toPaymentDestinationBody } from '../models/bankAcccountModel'
import { makaluApi } from '../utils/makaluApiClient'
import { batchFetch } from '../utils/requestsUtils'
import { handleHttpError } from '../utils/queryUtils'
import { downloadAttachments, uploadFiles } from './uploadServices'
import { resolveInvoice } from './invoicesServices'
import { trackEvent } from '../utils/analyticsUtils'

interface GetLoans {
    companyId: string
    query?: QueryStringInput
    hasAttachments: boolean
    signal?: AbortSignal
}

export const listLoans = batchFetch(async (params: GetLoans): Promise<Page<Loan>> => {
    const response = await makaluApi.listPage('/companies/{companyId}/loans', {
        params: {
            path: { companyId: params.companyId },
            query: toListQuery({
                ...params.query,
                params: {
                    ...params.query?.params,
                    include: 'invoices'
                }
            })
        },
        signal: params.signal
    })
    const mappedLoans = await Promise.all(response.body.map((loan) => resolveLoan(loan, params.hasAttachments)))
    const sanitizedMappedLoans = mappedLoans.filter((loan): loan is Loan => !isNil(loan))
    return { ...response, body: sanitizedMappedLoans }
})

interface GetLoansByUserId {
    userId: string
    companyId: string
    query?: QueryStringInput
    hasAttachments: boolean
}

export const listLoansOfUser = async (params: GetLoansByUserId): Promise<Page<Loan>> => {
    const response = await makaluApi.listPage('/companies/{companyId}/loans', {
        params: {
            path: { companyId: params.companyId },
            query: toListQuery({
                ...params.query,
                filters: [...(params.query?.filters ?? []), Equal('userId', params.userId)],
                params: { ...params.query?.params, include: 'invoices' }
            })
        }
    })

    const mappedLoans = await Promise.all(response.body.map((invoice) => resolveLoan(invoice, params.hasAttachments)))
    const sanitizedMappedLoans = mappedLoans.filter((invoice): invoice is Loan => !isNil(invoice))
    return { ...response, body: sanitizedMappedLoans }
}

interface GetLoanById {
    companyId: string
    loanId: string
}

export const getLoan = async (params: GetLoanById): Promise<Loan | undefined> => {
    const { companyId, loanId } = params
    const response = await makaluApi.GET('/companies/{companyId}/loans/{loanId}', {
        params: { path: { companyId, loanId } }
    })
    const mappedLoan = await resolveLoan(response)

    return mappedLoan
}

interface GetLoansByInvoiceId {
    companyId: string
    invoiceId: string | undefined
}

export const listLoansOfInvoice = async (params: GetLoansByInvoiceId): Promise<Page<Loan> | undefined> => {
    if (isNil(params.invoiceId)) return

    const response = await makaluApi.listPage('/companies/{companyId}/loans', {
        params: {
            path: { companyId: params.companyId },
            query: toListQuery({ filters: [Equal('invoiceId', params.invoiceId)] })
        }
    })
    const mappedLoans = await Promise.all(response.body.map((invoice) => resolveLoan(invoice, true)))
    const sanitizedMappedLoans = mappedLoans.filter((invoice): invoice is Loan => !isNil(invoice))
    return { ...response, body: sanitizedMappedLoans }
}

export const createLoan = async (companyId: string, payload: CreateLoanPayload): Promise<LoanGateway | undefined> => {
    try {
        const files = await uploadFiles(payload.attachments, companyId)
        const formattedData: CreateLoanGateway = {
            amount: payload.amount,
            currency: payload.currency,
            invoiceId: payload.invoiceId,
            receivableId: payload.receivableId,
            userId: payload.userId,
            payoutLabel: payload.payoutLabel || undefined,
            attachments: files,
            paymentDestination: payload.paymentDestination
                ? toPaymentDestinationBody(toPaymentDestination(payload.paymentDestination))
                : undefined,
            guarantor: payload.guarantor
        }

        const response = await makaluApi.POST('/companies/{companyId}/loans', {
            params: { path: { companyId } },
            body: formattedData
        })
        trackEvent('Create_Loan')
        return response
    } catch (error) {
        return handleHttpError('createLoan', error)
    }
}

export const updateLoan = async (companyId: string, payload: UpdateLoanPayload): Promise<Loan | undefined> => {
    try {
        const files = await uploadFiles(payload.attachments, companyId)
        const formattedData: UpdateLoanGateway = {
            amount: payload.amount,
            userId: payload.userId,
            currency: payload.currency,
            invoiceId: payload.invoiceId,
            receivableId: payload.receivableId,
            payoutLabel: payload.payoutLabel || undefined,
            attachments: files,
            paymentDestination: payload.paymentDestination
                ? toPaymentDestinationBody(toPaymentDestination(payload.paymentDestination))
                : undefined,
            guarantor: payload.guarantor
        }

        const response = await makaluApi.PATCH('/companies/{companyId}/loans/{loanId}', {
            params: { path: { companyId, loanId: payload.loanId } },
            body: formattedData
        })
        trackEvent('Update_Loan')
        const mappedLoan = await resolveLoan(response)
        return mappedLoan
    } catch (error) {
        return handleHttpError('updateLoan', error)
    }
}

export const cancelLoan = async (payload: CancelLoan): Promise<unknown> => {
    const { companyId, loanId } = payload

    if (isNil(loanId) || isNil(companyId)) {
        throw new Error(`cancelLoan : Missing companyId "${companyId}" or loanId: "${loanId}"`)
    }

    const response = await makaluApi.POST('/companies/{companyId}/loans/{loanId}/cancel', {
        params: { path: { companyId, loanId } }
    })
    trackEvent('Update_Loan_Cancel')
    return response
}

export const deleteLoan = async (payload: DeleteLoan): Promise<unknown> => {
    const { companyId, loanId } = payload
    try {
        const response = await makaluApi.DELETE('/companies/{companyId}/loans/{loanId}', {
            params: { path: { companyId, loanId } }
        })
        trackEvent('Delete_Loan')
        return response
    } catch (error) {
        return handleHttpError('deleteLoan', error)
    }
}

const resolveLoan = async (data: LoanGateway | undefined, hasAttachments = true): Promise<Loan | undefined> => {
    if (isNil(data)) return undefined

    const invoice = await resolveInvoice(data.invoice, false)

    if (isNil(data.attachments) || isEmpty(data.attachments) || !hasAttachments) {
        return {
            ...data,
            attachments: undefined,
            invoice,
            isEditable: editableLoanStatuses.includes(data.status)
        }
    }

    const attachments = await downloadAttachments(data.attachments)
    const attachmentCount = attachments?.length ?? 0
    return {
        ...data,
        attachments: isEmpty(attachments) ? undefined : attachments,
        attachmentCount,
        invoice,
        isEditable: editableLoanStatuses.includes(data.status)
    }
}

export const createLoanPaymentRetry = async (
    payload: CreatePaymentRetry,
    companyId: Company['id']
): Promise<LoanGateway | undefined> => {
    const { loanId, paymentDestination } = payload
    const formattedPayload: CreatePaymentRetryGateway = {
        options: {
            preferInstantPayment: payload.preferInstantPayment,
            paymentDestination: toPaymentDestinationBody({
                accountNumber: paymentDestination.accountNumber,
                accountNumberType: paymentDestination.accountNumberType,
                routingNumber: paymentDestination.routingNumber || undefined,
                type: 'BANK_ACCOUNT'
            })
        }
    }
    const response = await makaluApi.POST('/companies/{companyId}/loans/{loanId}/retry-payment', {
        params: { path: { companyId, loanId } },
        body: formattedPayload
    })
    trackEvent('Update_Loan_Retry')
    return response
}
